// src/pages/Contact.js
// Public contact page at /contact — no login required.
// Clean minimal page with DF logo + contact form calling submitSupportRequest CF.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../assets/divine-frameworks-logo.svg';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '../utils/analytics';

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [tried, setTried] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTried(true);
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setStatus('sending');
    setErrorMsg('');

    try {
      const functions = getFunctions(undefined, 'us-west1');
      const submitRequest = httpsCallable(functions, 'submitSupportRequest');
      await submitRequest({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        source: 'public_contact',
        _honeypot: honeypot,
      });
      setStatus('success');
      trackEvent('contact_submitted', { source: 'public' });
    } catch (err) {
      console.error('Contact form submission failed:', err);
      const msg = err?.message || '';
      if (msg.includes('Too many requests')) {
        setErrorMsg('Too many requests. Please try again later.');
      } else {
        setErrorMsg('Something went wrong. Please try again or email us at support@divineframeworks.com');
      }
      setStatus('error');
    }
  };

  return (
    <>
    <Helmet>
      <title>Contact Us — Divine Frameworks</title>
      <meta name="description" content="Get in touch with the Divine Frameworks team. We're here to help practitioners and seekers." />
      <meta property="og:title" content="Contact Us — Divine Frameworks" />
      <meta property="og:description" content="Get in touch with the Divine Frameworks team." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://divineframeworks.com/contact" />
      <meta property="og:site_name" content="Divine Frameworks" />
    </Helmet>
    <div style={{ minHeight: '100vh', background: '#FAFAFA', fontFamily: "'Montserrat', sans-serif" }}>
      {/* Header */}
      <div style={{ padding: '32px 0 16px', textAlign: 'center' }}>
        <Link to="/" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '0', textDecoration: 'none' }}>
          <img src={Logo} alt="Divine Frameworks" style={{ height: '100px', width: 'auto' }} />
          <span style={{
            fontSize: '20px', fontWeight: 500, letterSpacing: '0.025em',
            textTransform: 'uppercase', color: '#111827',
          }}>
            Divine Frameworks
          </span>
        </Link>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 20px 60px' }}>

        {status === 'success' ? (
          <div style={{ textAlign: 'center', paddingTop: '40px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%', background: '#D1FAE5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Message sent!</h2>
            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>
              We'll be in touch within 1-2 business days.
            </p>
            <Link
              to="/"
              style={{
                display: 'inline-block', marginTop: '24px', fontSize: '14px',
                color: '#bb9749', fontWeight: 600, textDecoration: 'none',
              }}
            >
              Back to home
            </Link>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '16px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
                Get in touch
              </h1>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>
                Have a question about Divine Frameworks? We'd love to hear from you.
              </p>
            </div>

            {status === 'error' && (
              <div style={{
                padding: '12px 16px', marginBottom: '16px', background: '#FEF2F2',
                border: '1px solid #FECACA', borderRadius: '8px',
              }}>
                <p style={{ fontSize: '13px', color: '#B91C1C' }}>
                  {errorMsg || 'Something went wrong. Please try again or email us directly at support@divineframeworks.com'}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Honeypot — hidden from real users */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={status === 'sending'}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: '14px',
                    border: `1px solid ${tried && !name.trim() ? '#DC2626' : '#D1D5DB'}`, borderRadius: '8px', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {tried && !name.trim() && (
                  <p style={{ fontSize: '12px', color: '#DC2626', margin: '4px 0 0' }}>Please enter your name</p>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={status === 'sending'}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: '14px',
                    border: `1px solid ${tried && !email.trim() ? '#DC2626' : '#D1D5DB'}`, borderRadius: '8px', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {tried && !email.trim() && (
                  <p style={{ fontSize: '12px', color: '#DC2626', margin: '4px 0 0' }}>Please enter a valid email address</p>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
                  required
                  disabled={status === 'sending'}
                  rows={5}
                  placeholder="Tell us how we can help..."
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: '14px',
                    border: `1px solid ${tried && !message.trim() ? '#DC2626' : '#D1D5DB'}`, borderRadius: '8px', outline: 'none',
                    resize: 'none', boxSizing: 'border-box',
                  }}
                />
                {tried && !message.trim() && (
                  <p style={{ fontSize: '12px', color: '#DC2626', margin: '4px 0 0' }}>Please enter your message</p>
                )}
                <div style={{ textAlign: 'right', marginTop: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{message.length}/2000</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'sending' || !name.trim() || !email.trim() || !message.trim()}
                style={{
                  width: '100%', padding: '12px', fontSize: '14px', fontWeight: 700,
                  color: 'white', backgroundColor: '#bb9749', border: 'none',
                  borderRadius: '8px', cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                  opacity: status === 'sending' ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {status === 'sending' ? 'Sending...' : 'Send message'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
              <p style={{ fontSize: '13px', color: '#9CA3AF', lineHeight: 1.6 }}>
                We typically respond within 1-2 business days.
              </p>
              <a
                href="mailto:support@divineframeworks.com"
                style={{ fontSize: '13px', color: '#bb9749', fontWeight: 600, textDecoration: 'none' }}
              >
                support@divineframeworks.com
              </a>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default Contact;
